import type { Properties } from 'hast';
import type { Root } from 'mdast';
import { ContainerDirective } from 'mdast-util-directive';
import type { Plugin } from 'unified';
import { visit } from 'unist-util-visit';

interface DirectiveDataWithHast {
  hName?: string;
  hProperties?: Properties;
  [key: string]: unknown;
}

/**
 * Extract text content from directive children
 */
function extractTextContent(node: ContainerDirective): string {
  let text = '';

  function traverse(children: unknown[]): void {
    for (const child of children) {
      if (typeof child === 'object' && child !== null) {
        const childNode = child as {
          type?: string;
          value?: string;
          children?: unknown[];
        };
        if (childNode.type === 'text' && childNode.value) {
          text += childNode.value;
        } else if (childNode.children) {
          traverse(childNode.children);
        }
      }
    }
  }

  if (node.children) {
    traverse(node.children);
  }

  return text.trim();
}

/**
 * Remark plugin that transforms chart directives into Chart components
 *
 * Syntax:
 * ```markdown
 * :::chart{type="line" title="Revenue" xKey="month" yKey="amount"}
 * [
 *   {"month": "Jan", "amount": 1000},
 *   {"month": "Feb", "amount": 1500}
 * ]
 * :::
 * ```
 *
 * Attributes:
 * - type: Chart type (line, area, bar, column, pie, donut, scatter, bubble) (required)
 * - title: Chart title (optional)
 * - xKey: Key for X-axis data (required for most chart types)
 * - yKey: Key(s) for Y-axis data (comma-separated for multiple) (required for most types)
 * - config: Additional configuration as JSON string (optional)
 *
 * Content: JSON array of data objects
 *
 * @example
 * ```markdown
 * :::chart{type="line" title="Sales Over Time" xKey="date" yKey="sales"}
 * [
 *   {"date": "2024-01", "sales": 1000},
 *   {"date": "2024-02", "sales": 1500}
 * ]
 * :::
 * ```
 */
export const remarkChart: Plugin<[], Root> = () => (tree) => {
  const visitDirective = (node: ContainerDirective) => {
    if (node.name !== 'chart') {
      return;
    }

    // Extract attributes
    const type = node.attributes?.type
      ? String(node.attributes.type)
      : undefined;
    const title = node.attributes?.title
      ? String(node.attributes.title)
      : undefined;
    const xKey = node.attributes?.xKey
      ? String(node.attributes.xKey)
      : undefined;
    const yKey = node.attributes?.yKey
      ? String(node.attributes.yKey)
      : undefined;
    const config = node.attributes?.config
      ? String(node.attributes.config)
      : undefined;

    // Validate required attributes
    if (!type) {
      return;
    }

    // Extract JSON data from children
    const textContent = extractTextContent(node);
    let data: unknown;

    try {
      data = textContent ? JSON.parse(textContent) : [];
    } catch {
      return;
    }

    // Validate data is an array
    if (!Array.isArray(data)) {
      return;
    }

    // Parse config if present
    let parsedConfig: Record<string, unknown> | undefined;
    if (config) {
      try {
        parsedConfig = JSON.parse(config) as Record<string, unknown>;
      } catch {
        // Ignore invalid config
      }
    }

    // Transform the directive node into a chart component node
    // react-markdown will map this to the Chart React component via the components prop
    const nodeData = (node.data || {}) as DirectiveDataWithHast;
    nodeData.hName = 'chart';
    nodeData.hProperties = {
      type,
      title,
      xKey,
      yKey,
      data: JSON.stringify(data),
      config: parsedConfig ? JSON.stringify(parsedConfig) : undefined,
    };
    node.data = nodeData;

    // Clear children since we've extracted the data
    node.children = [];
  };

  // Only process containerDirective (block-level charts)
  visit(tree, 'containerDirective', visitDirective);
};
